import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useCustomerForms } from "../../viewmodels/forms/useCustomerForms";
import { formsQuery } from "../../shared/customerForms";
import type { FormType } from "../../types/forms";
import CustomerFormFilters from "../../components/customerForms/CustomerFormFilters";
import FormFilterSelect from "../../components/customerForms/FormFilterSelect";
import FormsList from "../../components/customerForms/FormsList";
import NewCustomerForm from "../../components/customerForms/NewCustomerForm";
import EmptyModal from "../../components/modals/EmptyModal";
import { buttonClass, secondaryClass } from "../../components/customerForms/FormControls";

export default function FormsScreen({ customerId }: { customerId?: number }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const { page, options, loading, error, refresh } = useCustomerForms(
    formsQuery(params, customerId).toString()
  );
  const [newForm, setNewForm] = useState<FormType | null>(null);
  const [busy, setBusy] = useState(false);
  const returnTo = location.pathname + location.search;
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    if (key !== "page") next.delete("page");
    if (key === "type") next.delete("status");
    setParams(next, { replace: true });
  };
  return (
    <section className={customerId ? "space-y-6" : "p-4 sm:p-6 space-y-6"}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Forms</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track credit and demo requests from submission to completion.
          </p>
        </div>
        <div className="flex gap-2">
          <button className={secondaryClass} disabled={!options} onClick={() => setNewForm("demo")}>
            New Demo Request
          </button>
          <button className={buttonClass} disabled={!options} onClick={() => setNewForm("credit")}>
            New Credit Request
          </button>
        </div>
      </header>
      <CustomerFormFilters
        params={params}
        options={options}
        customerId={customerId}
        onChange={update}
        onApply={(values) => {
          const next = new URLSearchParams(params);
          Object.entries(values).forEach(([key, value]) => {
            if (customerId && key === "customer_id") return;
            value ? next.set(key, value) : next.delete(key);
          });
          next.delete("page");
          setParams(next, { replace: true });
        }}
        onClear={() => {
          const next = new URLSearchParams(params);
          [
            "type",
            "status",
            "search",
            "customer_id",
            "created_by",
            "date_from",
            "date_to",
            "page",
          ].forEach((key) => next.delete(key));
          setParams(next, { replace: true });
        }}
      />
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 p-4 text-red-800">
          {error}{" "}
          <button onClick={refresh} className="underline">
            Try again
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
        <p className="text-sm text-gray-500">
          {page && !loading ? `${page.total} ${page.total === 1 ? "form" : "forms"}` : "Forms"}
        </p>
        <div className="w-48">
          <FormFilterSelect
            compact
            label="Sort forms"
            value={`${params.get("sort") || "created_at"}:${params.get("direction") || "desc"}`}
            options={[
              { value: "created_at:desc", label: "Newest first" },
              { value: "created_at:asc", label: "Oldest first" },
              { value: "updated_at:desc", label: "Recently updated" },
              { value: "customer_name:asc", label: "Customer A–Z" },
              { value: "status:asc", label: "Status A–Z" },
            ]}
            onChange={(value) => {
              const [sort, direction] = value.split(":");
              const next = new URLSearchParams(params);
              next.set("sort", sort);
              next.set("direction", direction);
              next.delete("page");
              setParams(next, { replace: true });
            }}
          />
        </div>
      </div>
      {loading ? (
        <p role="status">Loading forms…</p>
      ) : (
        page && (
          <>
            {!page.data.length ? (
              <div className="rounded-xl border border-gray-200 bg-white p-12 text-center text-gray-500">
                No forms match your filters. Create a request or adjust the filters.
              </div>
            ) : (
              <FormsList forms={page.data} returnTo={returnTo} />
            )}
            <div className="flex items-center justify-between gap-3 text-sm">
              <p>
                {page.total} forms · Page {page.current_page} of {page.last_page}
              </p>
              <div className="flex gap-2">
                <button
                  className={secondaryClass}
                  disabled={page.current_page <= 1}
                  onClick={() => update("page", String(page.current_page - 1))}
                >
                  Previous
                </button>
                <button
                  className={secondaryClass}
                  disabled={page.current_page >= page.last_page}
                  onClick={() => update("page", String(page.current_page + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )
      )}
      <EmptyModal
        open={newForm !== null}
        dismissible={!busy}
        setOpen={() => {
          if (!busy) setNewForm(null);
        }}
        childComponent={
          newForm && options ? (
            <NewCustomerForm
              options={options}
              customerId={customerId}
              initialType={newForm}
              returnTo={returnTo}
              onClose={() => setNewForm(null)}
              onBusy={setBusy}
            />
          ) : null
        }
      />
    </section>
  );
}
